import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BottomSheet from '../mobile/BottomSheet'
import type { AgentDecision, AgentContextSnapshot, EvidenceSourceRow } from '../../lib/agentDecisions'
import { decisionTitle } from '../../lib/agentDecisions'
import { supabase } from '../../lib/supabaseClient'

type AgentDecisionDetailSheetProps = {
  decision: AgentDecision | null
  open: boolean
  onClose: () => void
  onAccept: (decisionId: string) => Promise<void>
  onSkip: (decisionId: string) => Promise<void>
}

function contextSignals(
  actionType: AgentDecision['action_type'],
  ctx: AgentContextSnapshot,
): Array<{ label: string; value: string; flag?: boolean }> {
  const signals: Array<{ label: string; value: string; flag?: boolean }> = []

  if (actionType === 'volume_reduce' || actionType === 'deload_week') {
    if (ctx.load.acwr != null) {
      signals.push({
        label: 'Training load (ACWR)',
        value: ctx.load.acwr.toFixed(2),
        flag: ctx.load.acwr > 1.5,
      })
    }
    signals.push({ label: 'Recovery score', value: `${ctx.load.load_balance_score}/100` })
    if (ctx.sleep.duration_hours != null) {
      signals.push({ label: 'Sleep last night', value: `${ctx.sleep.duration_hours.toFixed(1)}h` })
    }
    signals.push({ label: 'Sessions this week', value: String(ctx.load.sessions_last_7_days) })
  }

  if (actionType === 'exercise_swap') {
    signals.push({
      label: 'Orthopedic flags',
      value: ctx.user_model.orthopedic_flags.length > 0 ? ctx.user_model.orthopedic_flags.join(', ') : 'None',
    })
    signals.push({
      label: 'Session type today',
      value: ctx.load.session_type_today?.replace(/_/g, ' ') ?? 'Unknown',
    })
  }

  if (actionType === 'deload_week') {
    signals.push({
      label: 'Deload signal',
      value: ctx.predictive.deload_signal ?? 'None',
    })
    signals.push({
      label: 'Consecutive hard days',
      value: String(ctx.predictive.consecutive_hard_days),
    })
  }

  if (signals.length === 0 && ctx.load.acwr != null) {
    signals.push({ label: 'Training load (ACWR)', value: ctx.load.acwr.toFixed(2) })
  }

  return signals
}

function evidenceFromIds(_ids: string[], remote: EvidenceSourceRow[]): EvidenceSourceRow[] {
  return remote
}

const AgentDecisionDetailSheet = ({
  decision,
  open,
  onClose,
  onAccept,
  onSkip,
}: AgentDecisionDetailSheetProps) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [remoteEvidence, setRemoteEvidence] = useState<EvidenceSourceRow[]>([])

  useEffect(() => {
    if (!decision?.evidence_ids?.length) {
      setRemoteEvidence([])
      return
    }

    void supabase
      .from('evidence_sources')
      .select('id, short_key, title, authors, publication_year, doi')
      .in('id', decision.evidence_ids)
      .then(({ data }) => {
        if (data?.length) setRemoteEvidence(data as EvidenceSourceRow[])
      })
  }, [decision?.evidence_ids])

  const title = decision ? decisionTitle(decision.action_type, decision.action_payload) : ''
  const signals = useMemo(
    () => (decision ? contextSignals(decision.action_type, decision.context_snapshot) : []),
    [decision],
  )

  const evidence = useMemo(() => {
    if (!decision) return []
    return evidenceFromIds(decision.evidence_ids, remoteEvidence)
  }, [decision, remoteEvidence])

  if (!decision) return null

  const handleAccept = async () => {
    setLoading(true)
    try {
      await onAccept(decision.id)
      onClose()
    } finally {
      setLoading(false)
    }
  }

  const handleSkip = async () => {
    setLoading(true)
    try {
      await onSkip(decision.id)
      onClose()
    } finally {
      setLoading(false)
    }
  }

  return (
    <BottomSheet open={open} onClose={onClose} title={t('coach.agent.detailTitle')} variant="dark">
      <div className="space-y-5 pb-2">
        <div>
          <p className="text-xs uppercase tracking-widest text-kilo-soft">{t('coach.agent.detailSubtitle')}</p>
          <h3 className="mt-2 text-lg font-semibold text-kilo-text">{title}</h3>
          <p className="mt-3 text-sm leading-relaxed text-kilo-muted">{decision.reasoning}</p>
        </div>

        {signals.length > 0 ? (
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-kilo-soft">
              {t('coach.agent.signalsTitle')}
            </p>
            <ul className="mt-3 space-y-2">
              {signals.map((signal) => (
                <li key={signal.label} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-kilo-muted">{signal.label}</span>
                  <span className={signal.flag ? 'font-medium text-amber-400' : 'text-kilo-text'}>
                    {signal.value}
                    {signal.flag ? ' ⚠' : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {evidence.length > 0 ? (
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-kilo-soft">
              {t('coach.agent.basedOnTitle')}
            </p>
            <ul className="mt-3 space-y-3">
              {evidence.map((src) => (
                <li key={src.id} className="text-sm leading-relaxed text-kilo-muted">
                  {src.authors} ({src.publication_year}) — {src.title}
                  {src.doi ? `. DOI: ${src.doi}` : ''}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="space-y-2 pt-2">
          <button
            type="button"
            disabled={loading}
            onClick={() => void handleAccept()}
            className="flex h-11 w-full items-center justify-center rounded-xl bg-[#00C853] text-sm font-bold text-black disabled:opacity-50"
          >
            {t('coach.agent.acceptRecommendation')}
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={() => void handleSkip()}
            className="flex h-11 w-full items-center justify-center rounded-xl border border-white/10 text-sm font-medium text-kilo-text disabled:opacity-50"
          >
            {t('coach.agent.skipForToday')}
          </button>
        </div>
      </div>
    </BottomSheet>
  )
}

export default AgentDecisionDetailSheet
